<template>
  <v-app>
    <!-- HEADER BAR -->
    <HeaderBar
      :contacts-loading="contactsLoading"
      :inscription-contacts="inscriptionContacts"
      :error-code="errorCode"
      :enroll-id="enrollId"
      :invalid-contact="invalidContact"
      :metier="metier"
      :is-saving="isSaving"
      :is-dirty="isDirty"
      @save="save(undefined, true)"
    />

    <v-main class="page-bg">
      <v-container max-width="1100" class="py-8">

        <!-- WELCOME CARD -->
        <WelcomeCard v-if="!errorCode" :enroll-id="enrollId" :client="client" :loading="enrollLoading" />

        <!-- ERROR STATE -->
        <v-card v-if="errorCode" class="pa-8 text-center" elevation="0">
          <v-icon size="64" color="error" class="mb-4">error_outline</v-icon>
          <div class="text-h6 mb-2">Code introuvable</div>
          <div class="grey--text">{{ enrollCode ? `Le code ${enrollCode} est introuvable.` : 'Aucun code fourni.' }} Veuillez vérifier et réessayer.</div>
        </v-card>

        <template v-if="!errorCode">

          <!-- PLU -->
          <PluSection
            v-model="pluFile"
            :model="model"
            :code="enrollId"
            :loading="enrollLoading"
            :metier="metier"
          />

          <!-- COORDONNEES -->
          <template v-if="enrollLoading">
            <v-card class="section-card mb-4" elevation="0" v-for="n in 3" :key="'skel-section-' + n">
              <div class="section-header d-flex align-center pa-4">
                <v-skeleton-loader type="avatar" class="mr-3" />
                <div style="flex:1">
                  <v-skeleton-loader type="text" width="160" class="mb-1" />
                  <v-skeleton-loader type="text" width="220" />
                </div>
              </div>
              <v-divider />
              <v-card-text class="pa-6">
                <v-skeleton-loader type="paragraph" class="mb-3" />
                <v-skeleton-loader type="paragraph" />
              </v-card-text>
            </v-card>
          </template>
          <template v-else>
          <v-expansion-panels v-model="openPanels" multiple class="mb-4 section-panels">
            <ContactsSection
              :contacts="contact"
              :rules="rules"
              :is-saving="isSaving"
              @save="save()"
            />

            <ConfigurationSection
              :metier="metier"
              :tva.sync="tva"
              :cession.sync="cession"
              :city.sync="city"
              :cities="cities"
              :is-loading="isLoading"
              :citysearch.sync="citysearch"
              :update-city.sync="updateCity"
              :notilus="notilus"
              :current-black-printer="currentBlackPrinter"
              :black-printers="blackPrinters"
              :employees="employees"
              :fournisseurs="fournisseurs"
              :gln.sync="gln"
              :gln-enabled="!!client.idNotion"
              :gln-enseigne="glnEnseigne"
              @save-gln="saveGln"
              @select-etiquette="selectEtiquette"
              @add-employee="onAddEmployee"
              @remove-employee="removeEmployee"
              @add-fournisseur="onAddFournisseur"
              @remove-fournisseur="removeFournisseur"
            />

            <!-- GESTION / PLATEAUX -->
            <PlateauxSection
              v-if="metier.sections.plateaux"
              :plateaux="plateaux"
              :files="filesPlateaux"
              :model="model"
              :code="enrollId"
              :loading="filesLoading"
              @add="addPlateau"
              @remove="removePlateau"
              @update:files="filesPlateaux = $event"
            />

            <!-- LÉGISLATION -->
            <LegislationSection
              :nettoyage="fileNettoyage"
              :pms="filesPMS"
              :model="model"
              :code="enrollId"
              :loading="filesLoading"
              @update:nettoyage="fileNettoyage = $event"
              @update:pms="filesPMS = $event"
            />
          </v-expansion-panels>

          <!-- SAVE BUTTON -->
          <v-row justify="center" class="mt-4 mb-8">
            <v-col cols="auto">
              <v-btn color="primary" large :disabled="isSaving" :loading="isSaving" @click="save(undefined, true)">
                <v-icon left>save</v-icon>
                Enregistrer tout
              </v-btn>
            </v-col>
          </v-row>
          </template><!-- end v-else enrollLoading -->

        </template>
      </v-container>
    </v-main>

    <ScrollTopButton />
  </v-app>
</template>

<script>
import EnrollService from '@/services/EnrollService'
import FournisseurService from '@/services/FournisseurService'
import EmployeeService from '@/services/EmployeeService'
import PlateauService from '@/services/PlateauxService'
import PLUService from '@/services/PLUService'
import PlateauxService from '@/services/PlateauxService'
import ModeleService from '@/services/ModeleService'
import NotilusService from '@/services/NotilusService'
import ContactService from '@/services/ContactService'
import CityService from '@/services/CityService'
import SettingsService from '@/services/SettingsService'
import NettoyageService from '@/services/NettoyageService'
import PMSService from '@/services/PMSService'
import NotionService from '@/services/NotionService'
import GlnService from '@/services/GlnService'
import { cleanGln, glnModeFor } from '@/utils/glnUtils'
import { matchTypeContact, normalizePhone, splitNotionName } from '@/utils/notionContactUtils'
import FileUpload from './FileUpload.vue'
import ScrollTopButton from './sections/ScrollTopButton.vue'
import WelcomeCard from './sections/WelcomeCard.vue'
import HeaderBar from './sections/HeaderBar.vue'
import PluSection from './sections/PluSection.vue'
import PlateauxSection from './sections/PlateauxSection.vue'
import LegislationSection from './sections/LegislationSection.vue'
import ContactsSection from './sections/ContactsSection.vue'
import ConfigurationSection from './sections/ConfigurationSection.vue'
import moment from 'moment'
import { getMetierFromCode } from '@/config/metiers'
import { setMetier } from '@/utils/apiUtils'
export default {
  components: { FileUpload, ScrollTopButton, WelcomeCard, HeaderBar, PluSection, PlateauxSection, LegislationSection, ContactsSection, ConfigurationSection },
  data () {
    return {
      alert: false,
      isSaving: false,
      isDirty: false,
      _loadingData: false,
      rules: {
        required: (value) => !!value || 'Requis.',
        phoneCounter: (value) => (value && value.length <= 10) || '10 caractères max',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'E-mail invalide.'
        },
      },
      employees: [],
      contact: [],
      fournisseurs: [],
      client: [],
      step: 1,
      enrollId: '',
      enrollCode: '',
      currentStep: '',
      enroll: {},
      plateaux: [],
      notilus: [],
      filesPlateaux: { files: [] },
      fileNettoyage: { files: [] },
      filesPMS: { files: [] },
      citysearch: '',
      tva: 5.5,
      gln: '',
      glnInitial: '',
      glnEnseigne: '',
      city: {},
      cities: [],
      isLoading: false,
      pageLoading: false,
      contactsLoading: false,
      enrollLoading: false,
      filesLoading: false,
      errorCode: false,
      cession: '',
      updateCity: false,
      dateselector: false,
      menu: false,
      month: moment().subtract(1, 'year').format('YYYY-MM'),
      pluFile: { traditionnel: { files: [] }, libreService: { files: [] }, recettes: { files: [] } },
      blackPrinters: [
        { idPrinter: 1, nom: 'Evolis Primacy', etiquette: 3 },
        { idPrinter: 2, nom: 'Evolis Edikio', etiquette: 3 },
        { idPrinter: 3, nom: 'Evolis Digi', etiquette: 3 },
        { idPrinter: 4, nom: 'Zebra ZXP 3', etiquette: 7 },
      ],
      currentBlackPrinter: '',
      inscriptionContacts: [],
      notionContacts: [],
      openPanels: [0],
      model: {
        cacolumn: 'CA TTC',
        eancolumn: 'Code EAN',
        plucolumn: 'PLU',
        namecolumn: 'NOM',
        clientid: '',
        idmodel: '',
        plustart: null,
        pluend: null,
      },
    }
  },
  filters: {
    capitalize: function (value) {
      return value[0].toUpperCase() + value.slice(1)
    },
    formatMonth: function (value) {
      return moment(value, 'YYYY-MM').format('MMMM YYYY')
    },
    formatDate: function (value) {
      return moment(value.date).format('DD/MM/YYYY')
    }
  },
  created () {
    moment.locale('fr')
    if (this.$route.params.id) {
      const rawCode = this.$route.params.id
      this.enrollCode = rawCode
      this.enrollId = /^\d+$/.test(rawCode) ? parseInt(rawCode, 10) : rawCode
      setMetier(getMetierFromCode(rawCode).key)
      this.applyMetierTheme()
      this.checkEnroll(this.enrollId)
    } else {
      this.errorCode = true
    }
    // Handle page reload instead of destroyed() when user leaves
    window.addEventListener('beforeunload', () => {
      this.save()
    })
  },
  methods: {
    stepper: function (step) {
      return this.currentStep >= step
    },
    getOrSetModele (clientId) {
      ModeleService.get(clientId).then((res) => {
        if (res !== '') {
          this.model = { ...res, clientid: clientId }
          PLUService.get(clientId)
            .then((res) => {
              if (!Array.isArray(res) || res.length === 0) return
              const toSlot = (type) => ({
                files: res
                  .filter(r => (r.type || 'traditionnel') === type)
                  .map(r => ({ ...r, nom: r.nom, xlsxId: r.xlsx, uploaded: true }))
              })
              this.pluFile = {
                traditionnel: toSlot('traditionnel'),
                libreService: toSlot('libre-service'),
                recettes: toSlot('recettes'),
              }
            })
            .catch(err => console.error('[PLU] erreur chargement:', err))
        } else {
          ModeleService.set({ ...this.model, clientid: clientId }).then((res) => {
            this.model = { ...res, clientid: clientId }
          })
        }
      }).catch(err => console.error('[Modele] erreur chargement:', err))
    },
    getContactWithId (id) {
      return ContactService.getById(id).then((res) => {
        this.contact = res
      })
    },
    getContact () {
      return ContactService.get()
        .then((res) => {
          this.contact = res.map((contact) => {
            return {
              idtypecontact: contact.idtypecontact,
              type: contact.type,
              nom: '',
              prenom: '',
              telephone: '',
              mail: '',
            }
          }).sort((contactA, contactB) => contactA.idtypecontact - contactB.idtypecontact)
        })
    },
    applyNotionContacts () {
      if (!Array.isArray(this.contact) || this.contact.length === 0) return
      this.notionContacts.forEach((notionContact) => {
        const idtypecontact = matchTypeContact(notionContact.function)
        const card = this.contact.find((c) => c.idtypecontact === idtypecontact)
        if (!card) return
        const { nom, prenom } = splitNotionName(notionContact)
        const telephone = normalizePhone(notionContact.mobile || notionContact.phone)
        if (nom) card.nom = nom
        if (prenom) card.prenom = prenom
        if (telephone) card.telephone = telephone
        if (notionContact.email) card.mail = notionContact.email
      })
    },
    save (step, withProcess = false) {
      this.alert = true
      this.isSaving = true
      if (step >= 12) {
        EnrollService.setStep(this.enrollId, step)
        EnrollService.complete(this.enrollId).then(() => {
          this.checkEnroll(this.enrollId, true)
        })
      } else {
        EnrollService.register(this.enrollId, this.client.clientid)
          .then(() => Promise.all([
            ...this.fournisseurs.map((fournisseur) =>
              FournisseurService.upsert(this.enrollId, fournisseur)
            ),
            ContactService.upsert(
              this.client.idcontact || '',
              this.contact,
              this.client.clientid
            ),
            ...this.employees.map((employee) =>
              EmployeeService.upsert(this.enrollId, employee)
            ),
            NotilusService.upsert(
              this.enrollId,
              {
                tva: this.tva,
                codeville: this.city.codeville,
                imprimante: this.notilus.etiquette,
                modeleImprimante: this.notilus.modeleImprimante || null,
                cession: this.cession,
              },
              this.step
            ),
            SettingsService.save(this.enrollId, this.client.clientid, this.city.name, this.city.latitude, this.city.longitude),
            this.saveGln(),
          ])
            .then(() => {
              if (step !== undefined) {
                this.step = step
                EnrollService.setStep(this.enrollId, this.step)
              }
              if (withProcess) {
                return EnrollService.process(this.enrollId)
                  .then(() => this.checkEnroll(this.enrollId, true))
                  .catch(() => this.checkEnroll(this.enrollId, true))
              }
              return this.checkEnroll(this.enrollId, true)
            })
            .catch((err) => {
              console.error('[save] error:', err)
            })
            .finally(() => {
              this.isSaving = false
              this.isDirty = false
              this.$toast.success('Sauvegarde effectuée')
            })
          )
      }
      setTimeout(() => {
        this.alert = false
      }, 3000)
    },
    // N'écrit que si l'identifiant a changé et est valide pour l'enseigne ; jamais d'effacement
    saveGln () {
      const gln = cleanGln(this.gln)
      if (!this.client.idNotion || gln === this.glnInitial || !glnModeFor(this.glnEnseigne).isValid(gln)) {
        return Promise.resolve()
      }
      return GlnService.saveOne(this.metier.key, this.client.idNotion, gln)
        .then((result) => {
          if (result.saved) {
            this.glnInitial = gln
            this.$toast.success('GLN enregistré')
          } else {
            this.$toast.error('L\'enregistrement du GLN a échoué, merci de réessayer')
          }
        })
    },
    selectEtiquette (value) {
      const idEtiquette = value && value.etiquette ? this.blackPrinters.filter(printer => printer.idPrinter === value.idPrinter)[0].etiquette : value
      const modeleImprimante = value && value.nom ? value.nom : null
      if (idEtiquette === this.notilus.etiquette) {
        this.notilus = { ...this.notilus, etiquette: '', modeleImprimante: null }
      } else {
        this.notilus = { ...this.notilus, etiquette: idEtiquette, modeleImprimante }
      }
      this.currentBlackPrinter = value && value.etiquette ? value : ''
    },
    isValidEnrollCode (code) {
      if (code === null || code === undefined) return false
      const value = String(code)
      return /^\d{4}$/.test(value) || /^[A-Za-z]-\d{4}$/.test(value)
    },
    checkEnroll (code, silent = false) {
      if (this.isValidEnrollCode(code)) {
        if (!silent) this.enrollLoading = true
        this._loadingData = true
        return Promise.all([
          EnrollService.getEnrollment(code),
          EnrollService.get(code),
        ]).then((res) => {
          if (res[1].error) {
            this.errorCode = true
            this.enrollLoading = false
            return
          }
          this.currentStep = res[1].step
          this.step = res[1].step
          this.client = res[1]
          const contactsPromise = res[1].idNotion
            ? (this.contactsLoading = true, NotionService.getInscriptionContacts(res[1].idNotion)
              .then((emails) => { this.inscriptionContacts = emails })
              .finally(() => { this.contactsLoading = false }))
            : Promise.resolve()
          const notionContactsPromise = NotionService.getEnrollContacts(code)
            .then((contacts) => { this.notionContacts = contacts })
          // GLN de l'univers courant seulement : le formulaire est propre à un rayon
          const glnPromise = res[1].idNotion
            ? GlnService.getOne(this.metier.key, res[1].idNotion).then((store) => {
              if (store && !store.unavailable) {
                this.gln = store.gln || ''
                this.glnInitial = cleanGln(this.gln)
                this.glnEnseigne = store.enseigne || ''
              }
            })
            : Promise.resolve()
          this.getOrSetModele(this.client.clientid)
          this.notilus = res[0].notilus
          this.tva = this.notilus.tva
          if (res[0].notilus.cession !== null && res[0].notilus.cession !== undefined) {
            const cessionRaw = res[0].notilus.cession
            this.cession = (cessionRaw === true || cessionRaw === 1 || cessionRaw === '1' || cessionRaw === 'true') ? 'true' : 'false'
          }
          const settingsPromise = SettingsService.get(code).then(settings => {
            if (settings && settings.cityname) {
              this.city = {
                name: settings.cityname,
                codeville: this.notilus.codeville,
                latitude: settings.latitude,
                longitude: settings.longitude,
              }
            } else if (this.notilus.name) {
              this.city = { name: this.notilus.name, codeville: this.notilus.codeville }
            }
          })
          const contactPromise = res[1].idcontact
            ? this.getContactWithId(this.client.idcontact)
            : this.getContact()
          this.employees = res[0].employees || []
          this.plateaux = res[0].plateaux || []
          const plateauxFiles = (res[0].plateaux || []).filter(plateau => !!plateau.file).map(r => ({ ...r, nom: r.nom, uploaded: true }))
          this.filesPlateaux = { ...this.filesPlateaux, files: plateauxFiles }
          this.fournisseurs = (res[0].fournisseurs || []).map(fournisseur => fournisseur.iscentrale === true ? { ...fournisseur, icon: true } : { ...fournisseur, icon: false })
          if (res[0].notilus.imprimante) {
            this.notilus = { ...this.notilus, etiquette: res[0].notilus.imprimante, modeleImprimante: res[0].notilus.modeleImprimante || null }
            const imp = res[0].notilus.imprimante
            const matchedPrinter = this.blackPrinters.filter(p => p.etiquette === imp || p.idPrinter === imp)
            this.currentBlackPrinter = matchedPrinter.length > 0 ? matchedPrinter[0] : ''
          }
          if (!silent) this.enrollLoading = false
          this.filesLoading = !silent
          return Promise.all([
            NettoyageService.getByEnrollId(code),
            PMSService.getByEnrollId(code),
            PlateauxService.getByEnrollId(code),
            contactsPromise,
            notionContactsPromise,
            settingsPromise,
            contactPromise,
            glnPromise,
          ]).then(([nettoyageFiles, pmsFiles, plateauFiles]) => {
            if (Array.isArray(nettoyageFiles) && nettoyageFiles.length > 0) {
              this.fileNettoyage = { ...this.fileNettoyage, files: nettoyageFiles.map(r => ({ ...r, uploaded: true })) }
            }
            if (Array.isArray(plateauFiles) && plateauFiles.length > 0) {
              const plateauxFilesOnly = plateauFiles.filter(r => !!r.file)
              if (plateauxFilesOnly.length > 0) {
                this.filesPlateaux = { ...this.filesPlateaux, files: plateauxFilesOnly.map(r => ({ ...r, uploaded: true })) }
              }
            }
            if (Array.isArray(pmsFiles) && pmsFiles.length > 0) {
              this.filesPMS = { ...this.filesPMS, files: pmsFiles.map(r => ({ ...r, uploaded: true })) }
            }
            if (res[0].notilus.cession !== null && res[0].notilus.cession !== undefined && !this.cession) {
              const cessionRaw = res[0].notilus.cession
              this.cession = (cessionRaw === true || cessionRaw === 1 || cessionRaw === '1' || cessionRaw === 'true') ? 'true' : 'false'
            }
            this.applyNotionContacts()
            if (res[1].state === 'Completed') {
              this.enroll = Object.assign({}, res[1], {
                date: moment(res[1].createdate).format('DD/MM/YYYY'),
              })
            }
          }).finally(() => {
            this.filesLoading = false
            this._loadingData = false
          })
        })
      } else {
        this.errorCode = true
        this.step = 1
      }
    },
    removeEmployee (employee) {
      const employeeId = employee.idemployees || employee.idEmployees
      const employeesBeforeDelete = this.employees
      const filteredEmployees = this.employees.filter((existingEmployee) => {
        const existingEmployeeId = existingEmployee.idemployees || existingEmployee.idEmployees
        if (employeeId && existingEmployeeId) {
          return existingEmployeeId !== employeeId
        }
        return !(existingEmployee.nom === employee.nom && existingEmployee.prenom === employee.prenom)
      })
      this.employees = filteredEmployees

      if (!employeeId) {
        this.$toast.info('Employé(e) supprimé(e)')
        return Promise.resolve()
      }

      return EmployeeService.remove(this.enrollId, employee)
        .then(() => {
          this.$toast.info('Employé(e) supprimé(e)')
        })
        .catch(() => {
          this.employees = employeesBeforeDelete
          this.$toast.error('Erreur lors de la suppression de l\'employé(e)')
        })
    },
    removeFournisseur (fournisseur) {
      FournisseurService.remove(this.enrollId, fournisseur)
      this.fournisseurs = this.fournisseurs.filter((f) => f.nom !== fournisseur.nom)
      this.$toast.info('Fournisseur supprimé')
    },
    removePlateau (plateau) {
      PlateauService.remove(plateau, this.model.idmodel, this.enrollId)
        .then(() => {
          this.plateaux = this.plateaux.filter((plat) => plat.nom !== plateau.nom)
          this.filesPlateaux = { ...this.filesPlateaux, files: this.filesPlateaux.files.filter((plat) => plat.nom !== plateau.nom) }
          this.$toast.info('Plateau supprimé')
        })
    },
    onAddFournisseur (payload) {
      this.fournisseurs.push({
        nom: payload.nom,
        iscentrale: payload.iscentrale,
        isinternal: false,
      })
    },
    addPlateau (name, done) {
      return PlateauService.upsert(this.enrollId, { nom: name, date: moment().format('YYYY-MM-DD HH:mm:ss') })
        .then(() => {
          this.plateaux = this.plateaux.concat({ nom: name })
          if (typeof done === 'function') done()
          this.$toast.success('Plateau ajouté')
        })
    },
    onAddEmployee (payload) {
      this.employees.push({
        nom: payload.nom,
        prenom: payload.prenom,
      })
    },
    capitalize (s) {
      if (typeof s !== 'string') return ''
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
    fetchEntries (search) {
      CityService.get(this.capitalize(search))
        .then((response) => {
          this.cities = [
            ...new Set(response.map((x) => ({ name: x.name, codeville: x.codeville, latitude: x.latitude, longitude: x.longitude }))),
          ]
          this.isLoading = false
        })
        .finally(() => (this.isLoading = false))
    },
    clearEntries () {
      this.cities = []
    },
    fetchEntriesDebounced (val) {
      clearTimeout(this._searchTimerId)
      this._searchTimerId = setTimeout(() => {
        this.fetchEntries(val)
      }, 500) /* 500ms throttle */
    },
    applyMetierTheme () {
      const theme = this.$vuetify.theme.themes.light
      theme.primary = this.metier.theme.primary
      theme.secondary = this.metier.theme.secondary
    },
  },
  computed: {
    metier () {
      return getMetierFromCode(this.enrollCode)
    },
    minCaMonth: function () {
      return moment().subtract(1, 'year').format('YYYY-MM')
    },
    maxCaMonth: function () {
      return moment().subtract(1, 'month').format('YYYY-MM')
    },
    computedDate: function () {
      return moment(this.month, 'YYYY-MM').format('MMMM YYYY')
    },
    invalidContact () {
      return (
        this.contact.filter(
          (element) =>
            element.idtypecontact === 1 && (element.nom === '' || element.prenom === '')
        ).length > 0
      )
    },
  },
  watch: {
    contact: { deep: true, handler () { if (!this._loadingData) this.isDirty = true } },
    employees: { deep: true, handler () { if (!this._loadingData) this.isDirty = true } },
    fournisseurs: { deep: true, handler () { if (!this._loadingData) this.isDirty = true } },
    tva (val, old) { if (!this._loadingData && old !== undefined && val !== old) this.isDirty = true },
    gln (val, old) { if (!this._loadingData && old !== undefined && val !== old) this.isDirty = true },
    cession (val, old) { if (!this._loadingData && val !== old) this.isDirty = true },
    city: { deep: true, handler () { if (!this._loadingData) this.isDirty = true } },
    notilus: { deep: true, handler () { if (!this._loadingData) this.isDirty = true } },
    pluFile: { deep: true, handler () { this.isDirty = true } },
    citysearch (search) {
      if (!search) {
        return
      }
      this.clearEntries()
      this.isLoading = true
      this.fetchEntriesDebounced(search)
    },
  },
}
</script>
<style lang="scss">
@import "../assets/vars.scss";

.paytone-input .v-text-field__slot input {
  font-family: 'Paytone One', sans-serif !important;
}

// ── TITRES & HEADERS PANELS (globaux car dans des composants enfants) ─────────
.section-panel-header {
  background: linear-gradient(90deg, rgba($primary, 0.04) 0%, transparent 60%) !important;
  min-height: 64px !important;
  transition: background 0.2s ease, box-shadow 0.2s ease, border-left-color 0.2s ease;
  border-left: 3px solid transparent;
  cursor: pointer;

  &:hover {
    background: linear-gradient(90deg, color-mix(in srgb, var(--v-primary-base) 10%, transparent) 0%, color-mix(in srgb, var(--v-secondary-base) 5%, transparent) 100%) !important;
    box-shadow: 0 2px 10px color-mix(in srgb, var(--v-primary-base) 10%, transparent) !important;
    border-left-color: var(--v-primary-base);

    .panel-avatar {
      transform: scale(1.08);
      box-shadow: 0 3px 10px color-mix(in srgb, var(--v-primary-base) 28%, transparent);
    }
  }
}
.panel-avatar {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
}
.section-header {
  background: linear-gradient(90deg, color-mix(in srgb, var(--v-primary-base) 6%, transparent) 0%, transparent 100%);
  transition: background 0.2s ease, box-shadow 0.2s ease, border-left-color 0.2s ease;
  border-left: 3px solid transparent;

  &:hover {
    background: linear-gradient(90deg, color-mix(in srgb, var(--v-primary-base) 10%, transparent) 0%, color-mix(in srgb, var(--v-secondary-base) 5%, transparent) 100%);
    box-shadow: 0 2px 10px color-mix(in srgb, var(--v-primary-base) 10%, transparent);
    border-left-color: var(--v-primary-base);

    .v-avatar {
      transform: scale(1.08);
      box-shadow: 0 3px 10px rgba($primary, 0.28);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
  }

  .v-avatar {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
}
.section-title {
  font-weight: 700;
  font-size: 0.97rem;
  color: var(--v-primary-base);
  line-height: 1.2;
}
.section-subtitle {
  font-size: 0.8rem;
  color: rgba(0,0,0,0.45);
  margin-top: 2px;
}
</style>
<style lang="scss" scoped>
@import "../assets/app.scss";
@import "../assets/scroller.scss";

// ── PAGE BACKGROUND ──────────────────────────────────────────
.page-bg {
  background: #f4f7f9;
}

.section-card {
  border-radius: 12px !important;
  border: 1px solid #dde5ea !important;
  background: white;
}

// ── EXPANSION PANELS ─────────────────────────────────────────
.section-panels {
  border-radius: 12px !important;
  border: 1px solid #dde5ea !important;
  overflow: hidden;

  .v-expansion-panel {
    border-bottom: 1px solid #ecf0f3;
    &:last-child { border-bottom: none; }
    &::before { box-shadow: none !important; }
  }
}
// (styles section-panel-header, section-title, section-subtitle déplacés dans le bloc global non-scoped)
.section-sub-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
}

// ── CONTACT CARDS ────────────────────────────────────────────
.contact-card {
  border-radius: 10px !important;
  border: 1.5px solid #dde5ea !important;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: var(--v-secondary-base) !important;
    box-shadow: 0 3px 12px color-mix(in srgb, var(--v-secondary-base) 18%, transparent) !important;
  }
}
.contact-type-label {
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
}

// ── DAYS ─────────────────────────────────────────────────────
.days-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
}
.inline-days {
  display: flex;
  flex-wrap: wrap;
}
.day-check {
  display: flex;
  align-items: center;
  margin-right: 8px;
}
.day-label {
  font-size: 0.82rem;
  color: rgba(0,0,0,0.65);
  margin-left: 2px;
}

// ── CONFIG BLOCKS ─────────────────────────────────────────────
.config-block {
  border: 1px solid #e8eef2;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fafcfd;
  height: 100%;
}
.config-block-title {
  font-size: 0.83rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
}
.config-block-hint {
  font-size: 0.84rem;
  color: rgba(0,0,0,0.5);
  margin-top: 4px;
  margin-bottom: 4px;
}

// ── ÉTIQUETTES ───────────────────────────────────────────────
.etiquette-card {
  height: 100%;
  border-radius: 12px !important;
  border: 2px solid #dde5ea !important;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.esl-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 140px;
  svg {
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.12));
    transition: transform 0.4s ease;
    &:hover {
      transform: rotateY(20deg) scale(1.03);
    }
  }
}
.etiquette-card-title {
  color: rgba(0,0,0,0.55);
  font-weight: 700;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  justify-content: center;
  padding: 12px 16px 8px;
}
.selected-card {
  border: 3px solid var(--v-secondary-base) !important;
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--v-secondary-base) 18%, transparent) !important;
}
.selected-title {
  color: var(--v-secondary-base) !important;
}
.etiquette-container {
  padding: 8px 16px !important;
  text-align: center;
}
.etiquette-img {
  max-width: 100%;
  height: 200px;
  object-fit: contain;
  transition: transform 0.4s ease;

  &:hover {
    transform: rotateY(20deg) scale(1.03);
  }
}
.card-desc {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  font-size: 0.88rem;
  color: rgba(0,0,0,0.65);
  margin-top: 8px;
}

// ── LISTS (EMPLOYEES / FOURNISSEURS) ─────────────────────────
.employees-list {
  border: 1px solid #e8eef2 !important;
  background: #fafcfd;
  max-height: 280px;
  overflow-y: auto;
}
.employee-item {
  border-bottom: 1px solid #f0f4f6;
  &:last-child { border-bottom: none; }
}
.add-form-card {
  border-radius: 10px !important;
  border: 1px dashed color-mix(in srgb, var(--v-secondary-base) 70%, white) !important;
  background: color-mix(in srgb, var(--v-secondary-base) 4%, transparent);
}

// ── PLATEAU LIST ─────────────────────────────────────────────
.plateau-list {
  overflow-y: auto;
  max-height: 260px;
  border: 1px solid #e8eef2;
  border-radius: 8px;
  background: #fafcfd;
}

// ── MOUSE CURSOR ─────────────────────────────────────────────
.mouse {
  cursor: pointer;
}
</style>
